import React, { useCallback, useMemo } from "react";
import * as Scrivito from "scrivito";
import cx from "classnames";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import {
  ProductConfiguratorForm,
  ProductConfiguratorHeader,
} from "components/_ProductConfigurator/SubComponents";
import { useTranslate } from "hooks";
import { scrollToModalPart } from "utils";
import { Button } from "components/Button";
import { useProductConfiguratorStore } from "components/_ProductConfigurator/store/ProductConfiguratorStore";
import s from "./ConfiguratorModal.module.scss";

export const ProductConfigurationModal = NiceModal.create(
  Scrivito.connect(({ activatePartItem }) => {
    const translate = useTranslate();
    const modal = useModal();
    const productData = useProductConfiguratorStore.use.productData();
    const { setActivePartModal: setActivePart } =
      useProductConfiguratorStore.use.actions();
    const activePart = useProductConfiguratorStore.use.activePartModal();
    const isConfigurationModalOpened =
      useProductConfiguratorStore.use.isConfigurationModalOpened();

    const handleOpened = useCallback(() => {
      scrollToModalPart(activePart);
    }, [activePart]);

    const handleClose = useCallback(() => {
      modal.resolve();
      modal.hide();
      setActivePart(null);
    }, [modal, setActivePart]);

    const isMinimalContent = useMemo(() => {
      const parts = productData?.product?.orderCode?.parts ?? [];
      const result = parts.filter((part) => {
        return part.state < 2 || (part.state === 3 && part.presentation);
      });
      return result.some((res) => res.options?.length > 1) || result.length > 2;
    }, [productData]);

    return (
      <Modal
        isOpen={modal.visible}
        onClosed={handleClose}
        toggle={handleClose}
        size="xl"
        contentClassName="mh-100"
        onOpened={handleOpened}
      >
        <div
          className={cx("d-flex flex-column h-100", s.configuratorModalInner)}
        >
          <ModalHeader
            toggle={handleClose}
            tag="div"
            className="align-items-start pb-0 bg-white"
          >
            <ProductConfiguratorHeader
              activePart={activePart}
              isModalView={true}
              setActivePart={setActivePart}
              isTopologyView={false}
              activatePartItem={activatePartItem}
              isModalOpened={modal.visible}
              isConfigurationModalOpened={isConfigurationModalOpened}
            />
          </ModalHeader>

          <ModalBody
            className={cx("flex-grow-1  scroll-smooth", {
              "overflow-auto": isMinimalContent,
            })}
          >
            {modal.visible && productData?.product?.orderCode && (
              <ProductConfiguratorForm
                orderCode={productData.product.orderCode}
                activePart={activePart}
                isModalView={true}
                isConfigurationModalOpened={isConfigurationModalOpened}
                setActivePart={setActivePart}
              />
            )}
          </ModalBody>
          <ModalFooter className="d-flex align-items-center p-4 pb-2 w-100 bg-white">
            <Button
              data-testid="ProductConfiguratorModal_update-configuration-btn"
              className="m-0"
              onClick={handleClose}
            >
              {translate("PRODUCT_CONFIGURATOR_DONE")}
            </Button>
          </ModalFooter>
        </div>
      </Modal>
    );
  }),
);

// Must stay at module level — moving inside a component causes "No modal found" warnings on re-renders.
NiceModal.register("product-configuration-modal", ProductConfigurationModal);
